<template>
  <v-app>
    <!-- Floating Navigation Menu -->
    <div class="floating-nav" :class="{ 'nav-collapsed': !isNavOpen, 'rtl-nav': currentLocale === 'ar' }">
      <div class="nav-content" @mouseenter="isNavOpen = true" @mouseleave="isNavOpen = false">
        <div class="nav-header">
          <img :src="logo" alt="Kayan Logo" class="nav-logo" />
        </div>
        
        <div class="nav-links">
          <div v-for="(item, i) in navigationItems" 
               :key="i" 
               class="nav-item"
               @click="scrollTo(item.target)"
               :data-tooltip="currentLocale === 'ar' ? item.titleAr : item.titleEn"
          >
            <v-icon :icon="item.icon" :color="'#b78b44'"></v-icon>
            <span class="nav-text">
              {{ currentLocale === 'ar' ? item.titleAr : item.titleEn }}
            </span>
          </div>
        </div>

        <div class="nav-footer">
          <div class="nav-item" @click="toggleLocale">
            
              <v-icon icon="mdi-translate" color="white"></v-icon>
         
            <span class="nav-text">
              {{ currentLocale === 'ar' ? 'English' : 'عربي' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <v-app-bar class="d-md-none" flat>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-spacer></v-spacer>
      <img :src="logo" alt="Kayan Logo" class="mobile-logo" />
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" location="left" temporary class="mobile-nav">
      <!-- Mobile navigation content -->
    </v-navigation-drawer>

    <v-main>
      <!-- Hero Section -->
      <section id="home" class="hero-section">
        <div class="hero-background"></div>
        <div class="cyber-grid"></div>
        <div class="particles"></div>
        
        <div class="hero-content-wrapper">
          <div class="hero-main">
            <div class="tech-frame">
              <div class="glitch-container">
                <h1 class="cyber-glitch" :data-text="currentLocale === 'ar' ? 'كيان' : 'KAYAN'">
                  {{ currentLocale === 'ar' ? 'كــيان' : 'K A Y A N' }}
                </h1>
              </div>
              <div class="tech-line"></div>
              <h2 class="cyber-subtitle">
                {{ currentLocale === 'ar' ? 'للحلول التقنية' : 'TECH SOLUTIONS' }}
              </h2>
            </div>

            <div class="tech-cards">
              <div 
                v-for="(card, index) in techCards" 
                :key="index"
                class="tech-card"
                :style="{ '--delay': `${index * 0.1}s` }"
              >
                <div class="card-content">
                  <div class="card-icon">
                    <v-icon size="32" color="#b78b44">{{ card.icon }}</v-icon>
                  </div>
                  <h3>{{ currentLocale === 'ar' ? card.titleAr : card.titleEn }}</h3>
                  <p>{{ currentLocale === 'ar' ? card.descAr : card.descEn }}</p>
                </div>
                <div class="card-border"></div>
              </div>
            </div>

            <div class="hero-cta">
              <button class="cyber-button" @click="scrollTo('contact')">
                <span class="cyber-button__glitch"></span>
                <span class="cyber-button__tag">404</span>
                {{ currentLocale === 'ar' ? 'تواصل معنا' : 'Contact Us' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- About Section -->
      <section id="about" class="cyber-section">
        <div class="cyber-grid"></div>
        <div class="cyber-glow"></div>
        
        <v-container>
          <div class="cyber-content">
            <div class="section-header">
              <div class="cyber-title-box">
                <h2 class="cyber-title" 
                    :data-text="currentLocale === 'ar' ? 'من نحن' : 'Who We Are'"
                    :lang="currentLocale"
                >
                    {{ currentLocale === 'ar' ? 'من نحن' : 'Who We Are' }}
                </h2>
                <div class="title-line"></div>
              </div>
            </div>

            <v-row class="cyber-row" align="center">
              <v-col cols="12" md="6" class="cyber-text-col">
                <div class="cyber-text-box">
                  <div class="corner-decor top-left"></div>
                  <div class="corner-decor top-right"></div>
                  <div class="corner-decor bottom-left"></div>
                  <div class="corner-decor bottom-right"></div>
                  
                  <p class="cyber-text">
                    {{ currentLocale === 'ar' 
                      ? 'كيان هي شركة رائدة في مجال تكنولوجيا المعلومات، نقدم حلولاً مبتكرة تساعد الشركات على النمو في العصر الرقمي. نحن نجمع بين الخبرة التقنية والرؤية الاستراتيجية لتحقيق نتائج استثنائية لعملائنا.' 
                      : 'Kayan is a leading IT company providing innovative solutions that help businesses thrive in the digital age. We combine technical expertise with strategic vision to deliver exceptional results for our clients.' }}
                  </p>
                </div>
              </v-col>
              
              <v-col cols="12" md="6" class="cyber-image-col">
                <div class="cyber-image-frame">
                  <div class="frame-line top"></div>
                  <div class="frame-line right"></div>
                  <div class="frame-line bottom"></div>
                  <div class="frame-line left"></div>
                  
                  <v-img 
                    src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80" 
                    class="cyber-image"
                    :aspect-ratio="16/9"
                    cover
                  >
                    <template v-slot:placeholder>
                      <div class="cyber-loading">
                        <div class="loading-bar"></div>
                      </div>
                    </template>
                  </v-img>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </section>

      <div class="section-divider"></div>

      <!-- Services Section -->
      <section id="services" class="cyber-section">
        <div class="cyber-grid"></div>
        <div class="cyber-glow"></div>
        
        <v-container>
          <div class="cyber-content">
            <div class="section-header">
              <div class="cyber-title-box">
                <h2 class="cyber-title" :data-text="currentLocale === 'ar' ? 'خدماتنا' : 'Our Services'">
                  {{ currentLocale === 'ar' ? 'خدماتنا' : 'Our Services' }}
                </h2>
                <div class="title-line"></div>
              </div>
            </div>

            <div class="cyber-services-grid">
              <div 
                v-for="(service, i) in services" 
                :key="i"
                class="cyber-service-card"
                :style="{ '--delay': `${i * 0.1}s` }"
              >
                <div class="service-icon">
                  <div class="icon-border"></div>
                  <v-icon size="40" :color="'#b78b44'">{{ service.icon }}</v-icon>
                </div>
                <h3 class="service-title">
                  {{ currentLocale === 'ar' ? service.titleAr : service.titleEn }}
                </h3>
                <p class="service-desc">
                  {{ currentLocale === 'ar' ? service.descAr : service.descEn }}
                </p>
                <div class="card-lines">
                  <div class="line-1"></div>
                  <div class="line-2"></div>
                </div>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <div class="section-divider"></div>

      <!-- Clients Section -->
      <section id="clients" class="cyber-section">
        <div class="cyber-grid"></div>
        <div class="cyber-glow"></div>
        
        <v-container>
          <div class="cyber-content">
            <div class="section-header">
              <div class="cyber-title-box">
                <h2 class="cyber-title" :data-text="currentLocale === 'ar' ? 'عملاؤنا' : 'Our Clients'">
                  {{ currentLocale === 'ar' ? 'عملاؤنا' : 'Our Clients' }}
                </h2>
                <div class="title-line"></div>
              </div>
            </div>

            <div class="clients-slider" style="align-content: center;">
              <div class="slider-track">
                <!-- Duplicate the clients array to create seamless loop -->
                <div 
                  v-for="(client, i) in [...clients, ...clients]" 
                  :key="`${client.name}-${i}`"
                  class="client-slide"
                >
                  <div class="client-logo-wrapper">
                    <div class="logo-frame">
                      <div class="frame-corner top-left"></div>
                      <div class="frame-corner top-right"></div>
                      <div class="frame-corner bottom-left"></div>
                      <div class="frame-corner bottom-right"></div>
                      <v-img
                        :src="client.logo"
                        :alt="client.name"
                        height="80"
                        width="160"
                        contain
                        class="client-logo"
                      >
                        <template v-slot:placeholder>
                          <div class="cyber-loading">
                            <div class="loading-bar"></div>
                          </div>
                        </template>
                      </v-img>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <div class="section-divider"></div>

      <!-- Contact Section -->
      <section id="contact" class="cyber-section">
        <div class="cyber-grid"></div>
        <div class="cyber-glow"></div>
        
        <v-container>
          <div class="cyber-content">
            <div class="section-header">
              <div class="cyber-title-box">
                <h2 class="cyber-title" :data-text="currentLocale === 'ar' ? 'اتصل بنا' : 'Contact Us'">
                  {{ currentLocale === 'ar' ? 'اتصل بنا' : 'Contact Us' }}
                </h2>
                <div class="title-line"></div>
              </div>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <div class="cyber-form-container">
                  <div class="corner-decor top-left"></div>
                  <div class="corner-decor top-right"></div>
                  <div class="corner-decor bottom-left"></div>
                  <div class="corner-decor bottom-right"></div>
                  
                  <v-form ref="form" @submit.prevent="submitForm" class="cyber-form">
                    <div class="cyber-input-group">
                      <v-text-field
                        v-model="contactForm.name"
                        :label="currentLocale === 'ar' ? 'الاسم' : 'Name'"
                        :rules="formRules.name"
                        variant="outlined"
                        required
                        class="cyber-input"
                      ></v-text-field>
                      <div class="input-line"></div>
                    </div>

                    <div class="cyber-input-group">
                      <v-text-field
                        v-model="contactForm.email"
                        :label="currentLocale === 'ar' ? 'البريد الإلكتروني' : 'Email'"
                        :rules="formRules.email"
                        variant="outlined"
                        required
                        type="email"
                        class="cyber-input"
                      ></v-text-field>
                      <div class="input-line"></div>
                    </div>

                    <div class="cyber-input-group">
                      <v-textarea
                        v-model="contactForm.message"
                        :label="currentLocale === 'ar' ? 'الرسالة' : 'Message'"
                        :rules="formRules.message"
                        variant="outlined"
                        required
                        class="cyber-input"
                      ></v-textarea>
                      <div class="input-line"></div>
                    </div>

                    <button class="cyber-submit" type="submit" :disabled="formLoading">
                      <span class="submit-text">
                        {{ currentLocale === 'ar' ? 'إرسال' : 'Send' }}
                      </span>
                      <div class="submit-loader" v-if="formLoading">
                        <div class="loader-bar"></div>
                      </div>
                      <div class="submit-border"></div>
                    </button>
                  </v-form>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="cyber-contact-info">
                  <div class="info-item" v-for="(info, i) in contactInfo" :key="i">
                    <div class="info-icon">
                      <v-icon :color="'#b78b44'" size="24">{{ info.icon }}</v-icon>
                    </div>
                    <div class="info-content">
                      <h3>{{ currentLocale === 'ar' ? info.titleAr : info.titleEn }}</h3>
                      <div v-if="info.valueAr && info.valueEn" class="address-lines">
                        <p class="address-line" :class="{ 'rtl': currentLocale === 'ar' }">
                          {{ info.valueAr }}
                        </p>
                        <p class="address-line">{{ info.valueEn }}</p>
                      </div>
                      <p v-else>{{ info.value }}</p>
                    </div>
                    <div class="item-border"></div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </section>
    </v-main>

    <!-- Footer Styles -->
    <v-footer class="cyber-footer">
      <div class="footer-grid"></div>
      <div class="footer-glow"></div>
      
      <v-container>
        <v-row>
          <v-col cols="12" md="4" class="footer-logo-section">
            <img :src="logo" alt="Kayan Logo" class="footer-logo" />
            <p class="footer-tagline">
              {{ currentLocale === 'ar' ? 'مستقبل التكنولوجيا' : 'The Future of Technology' }}
            </p>
          </v-col>

          

          <v-col cols="12" md="4" class="footer-social">
         
            
              <div class="social-grid">
                <a 
                  v-for="social in socialLinks" 
                  :key="social.icon"
                  :href="social.link"
                  target="_blank"
                  class="social-icon"
                >
                  <div class="icon-box">
                    <v-icon :color="'#b78b44'">{{ social.icon }}</v-icon>
                  </div>
                </a>
              </div>
           
          </v-col>
          <v-col cols="12" md="4" class="footer-copyright"> <p class="copyright">
            © {{ new Date().getFullYear() }} KAYAN. 
            {{ currentLocale === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved' }}
          </p></v-col>
        </v-row>

       
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>

import logo from '@/assets/kayanlogo.png'
import client1 from '@/assets/client1.png'

export default {
  name: 'App',
  components: {
    
    
  },
  data: () => ({
    drawer: false,
    logo,
    currentLocale: 'en',
    navigationItems: [
      { 
        titleEn: 'Home', 
        titleAr: 'الرئيسية', 
        target: 'home',
        icon: 'mdi-home' 
      },
      { 
        titleEn: 'About Us', 
        titleAr: 'من نحن', 
        target: 'about',
        icon: 'mdi-information' 
      },
      { 
        titleEn: 'Services', 
        titleAr: 'خدماتنا', 
        target: 'services',
        icon: 'mdi-cog' 
      },
      { 
        titleEn: 'Our Clients', 
        titleAr: 'عملاؤنا', 
        target: 'clients',
        icon: 'mdi-account-group' 
      },
      { 
        titleEn: 'Contact', 
        titleAr: 'اتصل بنا', 
        target: 'contact',
        icon: 'mdi-email' 
      }
    ],
    services: [
      {
        icon: 'mdi-web',
        titleEn: 'Web Development',
        titleAr: 'تطوير المواقع',
        descEn: 'Custom web applications and responsive websites built with the latest technologies.',
        descAr: 'تطبيقات ويب مخصصة ومواقع متجاوبة مبنية بأحدث التقنيات.'
      },
      {
        icon: 'mdi-cellphone',
        titleEn: 'Mobile Apps',
        titleAr: 'تطبيقات الجوال',
        descEn: 'Native and cross-platform mobile applications for iOS and Android.',
        descAr: 'تطبيقات الهواتف الذكية لنظامي iOS و Android.'
      },
      {
        icon: 'mdi-cloud',
        titleEn: 'Cloud Solutions',
        titleAr: 'حلول سحابية',
        descEn: 'Cloud infrastructure and deployment solutions for scalable applications.',
        descAr: 'حلول البنية التحتية السحابية والنشر للتطبيقات القابلة للتوسع.'
      }
    ],
    socialLinks: [
      { icon: 'mdi-facebook', link: '#' },
      { icon: 'mdi-twitter', link: '#' },
      { icon: 'mdi-linkedin', link: '#' },
      { icon: 'mdi-instagram', link: '#' },
    ],
    contactForm: {
      name: '',
      email: '',
      message: ''
    },
    formRules: {
      name: [v => !!v || 'Name is required'],
      email: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      message: [v => !!v || 'Message is required']
    },
    formLoading: false,
    formSuccess: false,
    techCards: [
      {
        icon: 'mdi-rocket-launch',
        titleEn: 'Innovation',
        titleAr: 'ابتكار',
        descEn: 'Pushing boundaries with cutting-edge solutions',
        descAr: 'دفع الحدود بحلول متطورة'
      },
      {
        icon: 'mdi-shield-lock',
        titleEn: 'Security',
        titleAr: 'الأمان',
        descEn: 'Enterprise-grade protection for your data',
        descAr: 'حماية بمستوى المؤسسات لبياناتك'
      },
      {
        icon: 'mdi-trending-up',
        titleEn: 'Growth',
        titleAr: 'النمو',
        descEn: 'Scalable solutions for your business',
        descAr: 'حلول قابلة للتوسع لعملك'
      }
    ],
    clients: [
      {
        name: 'Client 1',
        logo: client1
      },
      {
        name: 'Client 1',
        logo: client1
      },
      {
        name: 'Client 1',
        logo: client1
      },
      {
        name: 'Client 1',
        logo: client1
      }
    ],
    isNavOpen: false,
    contactInfo: [
      {
        icon: 'mdi-map-marker',
        titleEn: 'Address',
        titleAr: 'العنوان',
        valueEn: 'Libya - Tripoli - Hay Al Andalus',
        valueAr: 'ليبيا - طرابلس - حي الأندلس'
      },
      {
        icon: 'mdi-phone',
        titleEn: 'Phone',
        titleAr: 'الهاتف',
        value: '+966 123 456 789'
      },
      {
        icon: 'mdi-email',
        titleEn: 'Email',
        titleAr: 'البريد الإلكتروني',
        value: 'info@kayan.com'
      }
    ]
  }),
  computed: {
    textAlignClass() {
      return this.currentLocale === 'ar' ? 'text-right' : 'text-left'
    }
  },
  mounted() {
    this.initSectionAnimations()
  },
  methods: {
    toggleLocale() {
      this.currentLocale = this.currentLocale === 'ar' ? 'en' : 'ar'
      document.dir = this.currentLocale === 'ar' ? 'rtl' : 'ltr'
    },
    scrollTo(targetId) {
      const element = document.getElementById(targetId);
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        
        window.scrollTo({
          top: middle,
          behavior: 'smooth'
        });

        // Close mobile drawer if open
        this.drawer = false;
      }
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return

      this.formLoading = true
      try {
        // Replace with your actual API endpoint
        await fetch('https://api.example.com/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.contactForm)
        })
        
        this.formSuccess = true
        this.contactForm = { name: '', email: '', message: '' }
        this.$refs.form.reset()
        
        setTimeout(() => {
          this.formSuccess = false
        }, 3000)
      } catch (error) {
        console.error('Form submission error:', error)
      } finally {
        this.formLoading = false
      }
    },
    initSectionAnimations() {
      const sections = document.querySelectorAll('section')
      
      const options = {
        root: null,
        threshold: 0.1,
        rootMargin: '-50px'
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('section-visible')
            entry.target.classList.remove('section-hidden')
            if (entry.target.querySelector('.stagger-item')) {
              entry.target.querySelectorAll('.stagger-item').forEach((item, index) => {
                item.style.animationDelay = `${index * 0.1}s`
                item.classList.add('item-visible')
              })
            }
          } else {
            entry.target.classList.add('section-hidden')
            entry.target.classList.remove('section-visible')
          }
        })
      }, options)

      sections.forEach(section => {
        section.classList.add('section-hidden')
        observer.observe(section)
      })
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

/* Replace all instances of 'cyan' with the new color. Update these color-related styles: */

/* Base color variables */
:root {
  --primary-color: #b78b44;
  --primary-glow: rgba(183, 139, 68, 0.2);
  --primary-transparent: rgba(183, 139, 68, 0.1);
}

/* Update glowing effects */
.cyber-glow {
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
}

/* Update borders and glows */
.cyber-section {
  box-shadow: 
    0 0 30px rgba(183, 139, 68, 0.15),
    0 0 60px rgba(183, 139, 68, 0.1),
    inset 0 0 30px rgba(183, 139, 68, 0.05);
  border: 1px solid rgba(183, 139, 68, 0.2);
}

/* Update grid lines */
.cyber-grid {
  background-image: 
    linear-gradient(to right, var(--primary-transparent) 1px, transparent 1px),
    linear-gradient(to bottom, var(--primary-transparent) 1px, transparent 1px);
}

/* Update text colors */
.cyber-title,
.service-title,
.info-content h3,
.footer-title,
.cyber-submit,
.nav-item:hover {
  color: var(--primary-color);
}

/* Update icon colors */
.v-icon {
  color: var(--primary-color) !important;
}

/* Update borders and lines */
.title-line,
.input-line,
.submit-border {
  background: var(--primary-color);
}

/* Update hover effects */
.cyber-service-card:hover,
.info-item:hover,
.cyber-box:hover {
  border-color: var(--primary-color);
  box-shadow: 0 0 30px var(--primary-glow);
}

/* Update gradients */
.footer-glow,
.loader-bar {
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
}

/* Update scanning line */
.clients-slider::after {
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
}

/* Update icon borders */
.icon-border,
.frame-corner {
  border-color: var(--primary-color);
}

/* Update hover states */
.icon-box:hover,
.cyber-link:hover {
  color: var(--primary-color);
  border-color: var(--primary-color);
}

/* Update button effects */
.cyber-button:hover {
  background: var(--primary-color);
  box-shadow: 0 0 30px var(--primary-glow);
}

/* Update form inputs */
.cyber-input :deep(.v-field__outline) {
  color: var(--primary-transparent) !important;
}

.cyber-input :deep(.v-label) {
  color: var(--primary-glow) !important;
}

/* Update navigation */
.floating-nav {
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(183, 139, 68, 0.2);
  box-shadow: 0 0 30px rgba(183, 139, 68, 0.1);
}

.nav-item:hover .nav-icon {
  background: rgba(183, 139, 68, 0.2);
}

/* Update footer elements */
.footer-divider {
  background: linear-gradient(90deg, transparent, rgba(183, 139, 68, 0.2), transparent);
}

.kayan-logo {
  width: 180px;
  height: 50px;
  object-fit: cover;
}

* {
  font-family: 'Cairo', sans-serif !important;
}

/* Update the hero section styles */
.hero-section {
  position: relative;
  min-height: 100vh;
  background: #faf7f2;
  overflow: hidden;
  margin: 0 10% 50px 10%;
  border-radius: 20px;
  box-shadow: 
    0 0 40px rgba(183, 139, 68, 0.15),
    0 0 80px rgba(183, 139, 68, 0.1),
    inset 0 0 40px rgba(183, 139, 68, 0.05);
  border: 1px solid rgba(183, 139, 68, 0.2);
}

/* Add animated patterns */
.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.3;
  background: 
    linear-gradient(45deg, transparent 48%, rgba(183, 139, 68, 0.1) 50%, transparent 52%),
    linear-gradient(-45deg, transparent 48%, rgba(183, 139, 68, 0.1) 50%, transparent 52%);
  background-size: 30px 30px;
  animation: patternMove 30s linear infinite;
}

.cyber-grid {
  background-image: 
    linear-gradient(to right, rgba(183, 139, 68, 0.1) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(183, 139, 68, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.2;
  animation: gridFloat 10s ease-in-out infinite;
}

.particles {
  background: radial-gradient(circle at 50% 50%, 
    rgba(183, 139, 68, 0.1) 0%, 
    transparent 50%
  );
  animation: pulseGlow 4s ease-in-out infinite;
}

/* Add these new animations */
@keyframes patternMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 60px 60px;
  }
}

@keyframes gridFloat {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.02);
  }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

/* Update content positioning */
.hero-content-wrapper {
  position: relative;
  z-index: 2;
  padding: 4rem 2rem;
}

/* Add subtle text shadow for better contrast */
.cyber-glitch, .cyber-subtitle {
  text-shadow: 1px 1px 2px rgba(183, 139, 68, 0.2);
}

.hero-main {
  text-align: center;
}

.tech-frame {
  position: relative;
  padding: 2rem;
  margin-bottom: 4rem;
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--primary-color),
      transparent
    );
  }
}

.tech-line {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 2px;
  background: var(--primary-color);
  box-shadow: 0 0 10px var(--primary-color);
}

.cyber-glitch {
  font-size: 6rem;
  font-weight: 900;
  color: var(--primary-color);
  text-transform: uppercase;
  position: relative;
  margin-bottom: 1rem;
  
  animation: titleShine 3s ease-in-out infinite;
  background: linear-gradient(
    120deg,
    var(--primary-color) 30%,
    #ffd700 50%,
    var(--primary-color) 70%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(183, 139, 68, 0.3);
}

.cyber-subtitle {
  font-size: 2rem;
  color: var(--primary-color);
  margin-top: 1rem;
 
  text-transform: uppercase;
  opacity: 0.9;
  text-shadow: 0 0 15px rgba(183, 139, 68, 0.2);
}

.tech-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
}

.tech-card {
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(183, 139, 68, 0.2);
  padding: 2rem;
  border-radius: 15px;
  transition: all 0.3s ease;
  animation: cardFloat 3s ease-in-out infinite;
  animation-delay: var(--delay);
  box-shadow: 0 4px 30px rgba(183, 139, 68, 0.1);
}

.tech-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 15px;
  padding: 2px;
  background: linear-gradient(
    45deg,
    transparent,
    rgba(183, 139, 68, 0.3),
    transparent
  );
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

.tech-card h3 {
  color: #b78b44;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.tech-card p {
  color: rgba(44, 44, 44, 0.9);
  font-size: 1rem;
  line-height: 1.5;
}

.tech-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(183, 139, 68, 0.5);
  box-shadow: 
    0 8px 32px rgba(183, 139, 68, 0.15),
    0 4px 8px rgba(183, 139, 68, 0.1);
}

.card-content {
  position: relative;
  z-index: 1;
}

.card-icon {
  margin-bottom: 1.5rem;
  position: relative;
}

.card-icon::after {
  content: '';
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(183, 139, 68, 0.1);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  filter: blur(8px);
}

.cyber-button {
  position: relative;
  background: transparent;
  border: 2px solid var(--primary-color);
  border-radius: 8px;
  padding: 1rem 3rem;
  font-size: 1.2rem;
  color: var(--primary-color);
  text-transform: uppercase;
  
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}

.cyber-button:hover {
  background: var(--primary-color);
  color: #000;
  box-shadow: 0 0 30px var(--primary-glow);
}

.cyber-button__glitch {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--primary-color);
  transform: translateX(-100%);
  animation: glitch 2s infinite;
}

.cyber-button__tag {
  position: absolute;
  top: -10px;
  right: -10px;
  background: var(--primary-color);
  color: #000;
  padding: 2px 8px;
  font-size: 0.8rem;
  border-radius: 4px;
  transform: rotate(15deg);
}

@keyframes textFlicker {
  0% { text-shadow: 2px 2px var(--primary-color); }
  100% { text-shadow: -2px -2px var(--primary-color); }
}

@keyframes cardFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes glitch {
  0% { transform: translateX(-100%); }
  10% { transform: translateX(100%); }
  11% { transform: translateX(-100%); }
}

@media (max-width: 768px) {
  .cyber-glitch {
    font-size: 4rem;
  }
  
  .cyber-subtitle {
    font-size: 1.5rem;
  }
  
  .tech-cards {
    grid-template-columns: 1fr;
  }
}

.v-btn {
  transition: var(--transition-smooth) !important;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2) !important;
}

.service-card {
  transition: var(--transition-smooth);
  border-radius: 16px !important;
  overflow: hidden;
  background: linear-gradient(145deg, #ffffff, #f5f5f5);
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: var(--card-shadow) !important;
}

.service-card .v-icon {
  transition: var(--transition-smooth);
  background: var(--primary-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transform-origin: center;
}

.service-card:hover .v-icon {
  transform: scale(1.1);
}

.carousel-content {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-form {
  border-radius: 16px;
  box-shadow: var(--card-shadow);
}

.v-text-field.v-text-field--outlined:hover > .v-input__control > .v-input__slot {
  border-color: var(--v-primary-base);
}

.social-links .v-btn {
  opacity: 0.8;
  transition: var(--transition-smooth);
}

.social-links .v-btn:hover {
  opacity: 1;
  transform: translateY(-5px);
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Loading animations */
.v-progress-circular {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

/* RTL Support */
[dir="rtl"] .v-application--wrap {
  flex-direction: row-reverse;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .text-h2 {
    font-size: 2rem !important;
  }
  .text-h3 {
    font-size: 1.75rem !important;
  }
  .text-h5 {
    font-size: 1.25rem !important;
  }
}

/* Parallax effect for hero section */
.hero-parallax {
  position: relative;
  transform: translateZ(-1px) scale(2);
  z-index: -1;
}

/* Gradient text effect */
.gradient-text {
  background: var(--primary-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}

/* Card hover effects */
.service-card {
  position: relative;
  z-index: 1;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--primary-gradient);
  opacity: 0;
  transition: var(--transition-smooth);
  z-index: -1;
  border-radius: 16px;
}

.service-card:hover::before {
  opacity: 0.05;
}

/* Floating animation for cards */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.floating {
  animation: float 3s ease-in-out infinite;
}

/* Section divider */
.section-divider {
  margin: 0 10%;
  height: 50px;
  position: relative;
  z-index: 2;
  background: linear-gradient(to bottom, 
    rgba(0, 0, 0, 1) 0%,
    rgba(183, 139, 68, 0.05) 50%,
    rgba(0, 0, 0, 1) 100%
  );
}

.section-divider::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--primary-gradient);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 0);
  opacity: 0.1;
}

/* Contact form enhancements */
.contact-form .v-text-field--outlined fieldset {
  transition: var(--transition-smooth);
}

.contact-form .v-text-field--outlined:hover fieldset {
  border-color: var(--v-primary-base) !important;
}

/* Pulse animation for CTA buttons */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

.pulse-button {
  animation: pulse 2s infinite;
}

/* Footer wave effect */
.footer-wave {
  position: relative;
  background: var(--primary-gradient);
}

.footer-wave::before {
  content: '';
  position: absolute;
  top: -50px;
  left: 0;
  right: 0;
  height: 50px;
  background: url('data:image/svg+xml,<svg viewBox="0 0 1200 120" xmlns="http://www.w3.org/2000/svg"><path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" fill="%231976D2" opacity=".25"/></svg>') no-repeat;
  background-size: cover;
}

/* Loading skeleton animation */
@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

/* Carousel text animations */
.slide-up {
  animation: slideUp 0.6s ease-out both;
  animation-delay: var(--delay, 0s);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.carousel-content h1 { --delay: 0.2s; }
.carousel-content h2 { --delay: 0.4s; }
.carousel-content p { --delay: 0.6s; }
.carousel-content .v-btn { --delay: 0.8s; }

/* Enhanced card hover effect */
.service-card {
  transform-style: preserve-3d;
  perspective: 1000px;
}

.service-card:hover {
  transform: translateY(-10px) rotateX(5deg);
}

.service-card .v-card-text {
  transform: translateZ(20px);
}

/* Enhanced section transitions */
section {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

section::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.1));
  pointer-events: none;
}

/* Enhanced form field focus effect */
.contact-form .v-field--focused {
  transform: scale(1.01);
  transition: transform 0.3s ease;
}

/* Enhanced social icons */
.social-links .v-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--primary-gradient);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
}

.social-links .v-btn:hover::before {
  opacity: 0.1;
}

/* Smooth page transitions */
.v-main {
  transition: opacity 0.3s ease;
  perspective: 1500px;
  overflow-x: hidden;
}

[dir="rtl"] .v-main {
  transition: opacity 0.3s ease;
}

/* Enhanced mobile menu */
.v-navigation-drawer {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
}

.v-list-item {
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 4px 8px;
}

.v-list-item:hover {
  background: var(--primary-gradient);
  color: white;
}

/* Section reveal animations */
.section-hidden {
  opacity: 0;
  transform: scale(1.1) translateY(50px);
  filter: blur(10px);
}

.section-visible {
  opacity: 1;
  transform: scale(1) translateY(0);
  filter: blur(0);
}

/* Stagger animations for items */
.stagger-item {
  opacity: 0;
  transform: translateY(20px);
}

.item-visible {
  animation: staggerReveal 0.6s cubic-bezier(0.5, 0, 0, 1) forwards;
}

@keyframes staggerReveal {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Enhanced section transitions */
section {
  position: relative;
  z-index: 1;
  overflow: hidden;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

section:hover {
  transform: scale(1.01);
}

/* Parallax background effect */
.parallax-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: var(--primary-gradient);
  opacity: 0.05;
  transform: translateZ(-1px) scale(2);
}

/* Enhanced divider animation */
.section-divider {
  position: relative;
  height: 100px;
  overflow: hidden;
}

.section-divider::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg viewBox="0 0 1200 120" xmlns="http://www.w3.org/2000/svg"><path d="M1200 0L0 0 598.97 114.72 1200 0z" fill="%231976D2" opacity=".1"/></svg>') no-repeat;
  background-size: cover;
  animation: wave 15s linear infinite;
}

@keyframes wave {
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-25%);
  }
  100% {
    transform: translateX(0);
  }
}

/* Enhanced card animations */
.service-card {
  transition: all 0.6s cubic-bezier(0.5, 0, 0, 1);
}

.service-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
}

/* Glowing effect for primary buttons */
.v-btn.v-btn--elevated {
  position: relative;
  overflow: hidden;
}

.v-btn.v-btn--elevated::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 60%);
  transform: rotate(45deg);
  transition: all 0.6s cubic-bezier(0.5, 0, 0, 1);
}

.v-btn.v-btn--elevated:hover::after {
  transform: rotate(45deg) translate(50%, 50%);
}

/* Text reveal animation */
.reveal-text {
  position: relative;
  overflow: hidden;
}

.reveal-text::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--primary-gradient);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.6s cubic-bezier(0.5, 0, 0, 1);
}

.reveal-text.visible::after {
  transform: scaleX(1);
}

/* Text stroke effects */
.text-stroke:not(.primary--text) {
  color: white;
  text-shadow: 
    -1px -1px 0 rgba(0,0,0,0.3),
    1px -1px 0 rgba(0,0,0,0.3),
    -1px 1px 0 rgba(0,0,0,0.3),
    1px 1px 0 rgba(0,0,0,0.3),
    0 2px 4px rgba(0,0,0,0.5);
}

.text-stroke-light {
  color: white;
  text-shadow: 
    -1px -1px 0 rgba(0,0,0,0.2),
    1px -1px 0 rgba(0,0,0,0.2),
    -1px 1px 0 rgba(0,0,0,0.2),
    1px 1px 0 rgba(0,0,0,0.2),
    0 1px 3px rgba(0,0,0,0.3);
}

/* Add these styles */
.v-carousel .v-btn--icon {
  background: rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;
}

.v-carousel .v-btn--icon:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

.v-carousel .v-btn--icon .v-icon {
  color: white;
  font-size: 2rem;
}

/* Make arrows more visible on mobile */
@media (max-width: 600px) {
  .v-carousel .v-btn--icon {
    transform: scale(0.8);
    background: rgba(255, 255, 255, 0.15);
  }
  
  .v-carousel .v-btn--icon:hover {
    transform: scale(0.9);
  }
}

/* Add these styles */
section {
  scroll-margin-top: 64px; /* Same as header height */
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

@media (max-width: 600px) {
  .hero-buttons {
    flex-direction: column;
  }
  
  .hero-cta {
    margin-right: 0 !important;
    margin-bottom: 1rem;
  }
}

/* Client section styles */
.client-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: white;
}

.client-card:hover {
  transform: translateY(-5px);
  border-color: var(--v-primary-base);
}

.client-card .v-img {
  transition: all 0.3s ease;
  filter: grayscale(100%);
  opacity: 0.7;
}

.client-card:hover .v-img {
  filter: grayscale(0%);
  opacity: 1;
}

.client-card .v-card-text {
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.client-card:hover .v-card-text {
  opacity: 1;
  transform: translateY(0);
}

/* Add/update these styles */
.v-carousel {
  background-color: black; /* Prevents white flash */
}

.v-carousel .v-window__container {
  background-color: black;
}

/* Smooth fade transition */
.v-carousel .v-window-item {
  transition: opacity 0.6s ease-in-out !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Make sure images cover properly */
.carousel-image {
  height: 100vh !important;
  width: 100% !important;
  object-fit: cover;
  transition: all 0.6s ease-in-out;
}

/* Ensure overlay transitions smoothly */
.carousel-overlay {
  transition: opacity 0.6s ease-in-out;
}

.logo {
  width: 200px;
  height: auto;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

/* For mobile */
@media (max-width: 600px) {
  .logo {
    width: 160px;
  }
}

/* Floating Navigation Menu */
.floating-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  left: 20px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px 15px;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 30px rgba(0, 255, 255, 0.2);
  width: 280px;
  border: 1px solid rgba(0, 255, 255, 0.2);
}

.nav-collapsed {
  width: 70px;
}

.rtl-nav {
  left: auto;
  right: 20px;
}

.nav-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.nav-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 255, 255, 0.1);
}

.nav-logo {
  width: 120px;
  height: auto;
  transition: all 0.3s ease;
  filter: brightness(1.2);
}

.nav-collapsed .nav-logo {
  width: 35px;
}

.nav-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s ease;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  position: relative;
  overflow: hidden;
}

.nav-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 255, 255, 0.1);
  transform: translateX(-100%);
  transition: transform 0.3s ease;
}

.nav-item:hover::before {
  transform: translateX(0);
}

.nav-item:hover {
  color: var(--primary-color);
  transform: translateX(5px);
}

.nav-item .v-icon {
  color: var(--primary-color) !important;
  transition: all 0.3s ease;
}

.nav-item:hover .v-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 0 5px var(--primary-color));
}

.nav-text {
  font-size: 1rem;
  font-weight: 500;
  opacity: 1;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.nav-collapsed .nav-text {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.nav-footer {
  margin-top: auto;
  border-top: 1px solid rgba(0, 255, 255, 0.1);
  padding-top: 10px;
}

/* Mobile Navigation */
.mobile-logo {
  height: 40px;
  width: auto;
}

.mobile-nav {
  background: rgba(0, 0, 0, 0.95);
}

@media (max-width: 768px) {
  .floating-nav {
    display: none;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 0.4; }
}

@keyframes gridMove {
  0% { transform: translateY(0); }
  100% { transform: translateY(50px); }
}

@keyframes glitch {
  0% { text-shadow: 0.05em 0 0 rgba(255,0,0,0.75),
                   -0.025em -0.05em 0 rgba(0,255,0,0.75),
                    0.025em 0.05em 0 rgba(0,0,255,0.75); }
  14% { text-shadow: 0.05em 0 0 rgba(255,0,0,0.75),
                    -0.025em -0.05em 0 rgba(0,255,0,0.75),
                     0.025em 0.05em 0 rgba(0,0,255,0.75); }
  15% { text-shadow: -0.05em -0.025em 0 rgba(255,0,0,0.75),
                     0.025em 0.025em 0 rgba(0,255,0,0.75),
                    -0.05em -0.05em 0 rgba(0,0,255,0.75); }
  49% { text-shadow: -0.05em -0.025em 0 rgba(255,0,0,0.75),
                     0.025em 0.025em 0 rgba(0,255,0,0.75),
                    -0.05em -0.05em 0 rgba(0,0,255,0.75); }
  50% { text-shadow: 0.025em 0.05em 0 rgba(255,0,0,0.75),
                     0.05em 0 0 rgba(0,255,0,0.75),
                     0 -0.05em 0 rgba(0,0,255,0.75); }
  99% { text-shadow: 0.025em 0.05em 0 rgba(255,0,0,0.75),
                     0.05em 0 0 rgba(0,255,0,0.75),
                     0 -0.05em 0 rgba(0,0,255,0.75); }
  100% { text-shadow: -0.025em 0 0 rgba(255,0,0,0.75),
                     -0.025em -0.025em 0 rgba(0,255,0,0.75),
                     -0.025em -0.05em 0 rgba(0,0,255,0.75); }
}

@keyframes glitch-anim {
  0% { clip: rect(44px, 450px, 56px, 0); }
  100% { clip: rect(44px, 450px, 56px, 0); }
}

@keyframes glitch-anim2 {
  0% { clip: rect(44px, 450px, 56px, 0); }
  100% { clip: rect(44px, 450px, 56px, 0); }
}

/* Add hover tooltip for collapsed nav */
.nav-collapsed .nav-item::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: var(--primary-color);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin-left: 10px;
  border: 1px solid rgba(0, 255, 255, 0.2);
}

.nav-collapsed .nav-item:hover::after {
  opacity: 1;
  transform: translateY(-50%) translateX(5px);
}

/* Cyber Section Styles */
.cyber-section {
  position: relative;
  background: rgba(0, 0, 0, 0.95);
  padding: 100px 0;
  overflow: hidden;
  margin: 0 10%;
  border-radius: 20px;
  box-shadow: 
    0 0 30px rgba(183, 139, 68, 0.15),
    0 0 60px rgba(183, 139, 68, 0.1),
    inset 0 0 30px rgba(183, 139, 68, 0.05);
  border: 1px solid rgba(183, 139, 68, 0.2);
  backdrop-filter: blur(10px);
  margin-bottom: 50px;
}

.cyber-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60%;
  height: 60%;
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
  pointer-events: none;
}

.cyber-content {
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.cyber-title-box {
  display: inline-block;
  position: relative;
  padding: 20px 40px;
}

.cyber-title {
  font-size: 3rem;
  color: var(--primary-color);
  text-transform: uppercase;
 
  margin: 0;
  position: relative;
  text-shadow: 0 0 10px var(--primary-glow);
}

.title-line {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 2px;
  background: var(--primary-color);
  box-shadow: 0 0 10px var(--primary-color);
}

.cyber-text-box {
  position: relative;
  padding: 30px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.corner-decor {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid var(--primary-color);
}

.top-left {
  top: -2px;
  left: -2px;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: -2px;
  right: -2px;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: -2px;
  left: -2px;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: -2px;
  right: -2px;
  border-left: none;
  border-top: none;
}

.cyber-text {
  color: white;
  font-size: 1.1rem;
  line-height: 1.8;
  margin: 0;
}

.cyber-image-frame {
  position: relative;
  padding: 20px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  overflow: hidden;
}

.frame-line {
  position: absolute;
  background: var(--primary-color);
  transition: all 0.3s ease;
}

.frame-line.top, .frame-line.bottom {
  height: 2px;
  width: 100%;
  left: 0;
}

.frame-line.left, .frame-line.right {
  width: 2px;
  height: 100%;
  top: 0;
}

.frame-line.top { top: 0; }
.frame-line.right { right: 0; }
.frame-line.bottom { bottom: 0; }
.frame-line.left { left: 0; }

.cyber-image {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.cyber-loading {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-bar {
  width: 50%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}

/* Hover Effects */
.cyber-text-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 0 30px rgba(0, 255, 255, 0.2);
  border-color: var(--primary-color);
}

.cyber-image-frame:hover .frame-line {
  box-shadow: 0 0 15px var(--primary-color);
}

.cyber-image-frame:hover .cyber-image {
  transform: scale(1.02);
}

@media (max-width: 960px) {
  .cyber-title {
    font-size: 2.5rem;
  }
  
  .cyber-text {
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .cyber-title {
    font-size: 2rem;
  }
  
  .cyber-row {
    flex-direction: column-reverse;
  }
  
  .cyber-text-col, .cyber-image-col {
    margin-bottom: 30px;
  }
}

/* Update/Add these styles */
.v-main {
  background-color: #faf7f2 !important; /* Warm white main background */
  perspective: 1500px;
  overflow-x: hidden;
}

section {
  transform-origin: center center;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.section-hidden {
  opacity: 0;
  transform: scale(1.1) translateY(50px);
  filter: blur(10px);
}

.section-visible {
  opacity: 1;
  transform: scale(1) translateY(0);
  filter: blur(0);
}

/* Add transition styles for each section type */
.hero-section.section-hidden {
  transform: scale(1.2) translateY(30px);
}

.cyber-section.section-hidden {
  transform: scale(1.15) rotateX(5deg);
}

/* Add a smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Optional: Add a transition overlay */
.section-transition-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, 
    transparent 0%, 
    rgba(183, 139, 68, 0.1) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s ease;
  z-index: 999;
}

.section-hidden + .section-transition-overlay {
  opacity: 1;
}

/* Enhance the section dividers */
.section-divider {
  height: 100px;
  position: relative;
  z-index: 2;
  background: linear-gradient(to bottom, 
    rgba(250, 247, 242, 1) 0%,
    rgba(183, 139, 68, 0.05) 50%,
    rgba(250, 247, 242, 1) 100%
  );
  transform-origin: top;
  transition: all 0.5s ease;
}

.section-hidden + .section-divider {
  transform: scaleY(1.5);
  opacity: 0;
}

/* Add smooth transitions for content */
.cyber-content,
.hero-content-wrapper,
.tech-cards {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

section.section-hidden .cyber-content,
section.section-hidden .hero-content-wrapper,
section.section-hidden .tech-cards {
  transform: translateY(30px);
  opacity: 0;
}

/* Enhance mobile transitions */
@media (max-width: 768px) {
  .section-hidden {
    transform: scale(1.05) translateY(30px);
  }
  
  .hero-section.section-hidden {
    transform: scale(1.1) translateY(20px);
  }
}

/* Services Section Styles */
.cyber-services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.cyber-service-card {
  position: relative;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  overflow: hidden;
  animation: cardFloat 3s ease-in-out infinite;
  animation-delay: var(--delay);
}

.cyber-service-card:hover {
  transform: translateY(-10px);
  border-color: var(--primary-color);
  box-shadow: 0 0 30px var(--primary-glow);
}

.service-icon {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid var(--primary-color);
  border-radius: 20px;
  transform: rotate(45deg);
  transition: all 0.3s ease;
}

.cyber-service-card:hover .icon-border {
  transform: rotate(225deg);
  box-shadow: 0 0 15px var(--primary-color);
}

.service-title {
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
  text-transform: uppercase;
 
}

.service-desc {
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  line-height: 1.6;
}

.card-lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.card-lines .line-1,
.card-lines .line-2 {
  position: absolute;
  background: rgba(0, 255, 255, 0.2);
}

.card-lines .line-1 {
  top: 0;
  left: 20%;
  width: 1px;
  height: 100%;
  transform: scaleY(0);
  transform-origin: top;
  transition: transform 0.3s ease;
}

.card-lines .line-2 {
  bottom: 20%;
  left: 0;
  width: 100%;
  height: 1px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.cyber-service-card:hover .line-1 {
  transform: scaleY(1);
}

.cyber-service-card:hover .line-2 {
  transform: scaleX(1);
}

/* Add glitch effect on hover */
.cyber-service-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent,
    rgba(0, 255, 255, 0.1),
    transparent
  );
  transform: rotate(45deg);
  animation: glitch-sweep 3s linear infinite;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.cyber-service-card:hover::before {
  opacity: 1;
}

@keyframes glitch-sweep {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(50%, 50%) rotate(360deg);
  }
}

@media (max-width: 768px) {
  .cyber-services-grid {
    grid-template-columns: 1fr;
  }
  
  .cyber-service-card {
    padding: 1.5rem;
  }
  
  .service-title {
    font-size: 1.25rem;
  }
}

/* Contact Section Styles */
.cyber-form-container {
  position: relative;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.cyber-form {
  position: relative;
  z-index: 2;
}

.cyber-input-group {
  position: relative;
  margin-bottom: 2rem;
}

.cyber-input {
  background: rgba(0, 0, 0, 0.3) !important;
  border-color: rgba(0, 255, 255, 0.2) !important;
  color: white !important;
}

.cyber-input :deep(.v-field__outline) {
  color: rgba(0, 255, 255, 0.2) !important;
}

.cyber-input :deep(.v-field__input) {
  color: white !important;
}

.cyber-input :deep(.v-label) {
  color: rgba(0, 255, 255, 0.7) !important;
}

.input-line {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--primary-color);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.cyber-input:hover + .input-line,
.cyber-input:focus + .input-line {
  transform: scaleX(1);
}

.cyber-submit {
  position: relative;
  width: 100%;
  padding: 1rem;
  background: transparent;
  border: none;
  color: var(--primary-color);
  font-size: 1.2rem;
  text-transform: uppercase;
 
  cursor: pointer;
  overflow: hidden;
}

.submit-text {
  position: relative;
  color: white;
  z-index: 2;
}

.submit-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid var(--primary-color);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.submit-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
}

.loader-bar {
  width: 50%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  animation: loading 1.5s infinite;
}

.cyber-submit:hover .submit-border {
  box-shadow: 0 0 20px var(--primary-color);
  transform: scale(1.02);
}

.cyber-contact-info {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.info-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.info-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--primary-color);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.info-content h3 {
  color: var(--primary-color);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.info-content p {
  color: white;
  margin: 0;
}

.info-item:hover {
  transform: translateX(10px);
  border-color: var(--primary-color);
  box-shadow: 0 0 30px var(--primary-glow);
}

.info-item:hover .info-icon {
  background: var(--primary-color);
}

.info-item:hover .info-icon .v-icon {
  color: black !important;
}

@media (max-width: 960px) {
  .cyber-contact-info {
    margin-top: 2rem;
  }
}

/* Client Section Styles */
.clients-slider {
  position: relative;
  width: 100%;
  height: 120px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  border: 1px solid rgba(183, 139, 68, 0.2);
  box-shadow: 
    0 10px 30px rgba(183, 139, 68, 0.15),
    0 20px 60px rgba(183, 139, 68, 0.1),
    inset 0 0 20px rgba(183, 139, 68, 0.05);
  transform: translateY(-5px);
}

.slider-track {
  display: flex;
  animation: slideTrack 30s linear infinite;
  width: fit-content;
}

.slider-track:hover {
  animation-play-state: paused;
}

.client-slide {
  flex: 0 0 200px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.client-logo-wrapper {
  position: relative;
  width: 160px;
  height: 80px;
  transition: all 0.3s ease;
}

.logo-frame {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(183, 139, 68, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(183, 139, 68, 0.1);
}

.frame-corner {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 1px solid var(--primary-color);
  transition: all 0.3s ease;
}

.top-left {
  top: -1px;
  left: -1px;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: -1px;
  right: -1px;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: -1px;
  left: -1px;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: -1px;
  right: -1px;
  border-left: none;
  border-top: none;
}

.client-logo {
  filter: grayscale(100%) brightness(1.2);
  opacity: 0.7;
  transition: all 0.5s ease;
}

.client-logo-wrapper:hover .client-logo {
  filter: grayscale(0%) brightness(1); /* Remove grayscale and adjust brightness on hover */
  opacity: 1;
}

.client-logo-wrapper:hover .logo-frame {
  border-color: #b78b44;
  box-shadow: 
    0 10px 20px rgba(183, 139, 68, 0.2),
    0 5px 15px rgba(183, 139, 68, 0.15);
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 1); /* Full white background on hover */
}

@keyframes slideTrack {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Add scanning line effect */
.clients-slider::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  animation: scanLine 2s linear infinite;
}

@keyframes scanLine {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(120px);
  }
}

/* Add fade edges */
.clients-slider::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    #faf7f2 0%,
    transparent 5%,
    transparent 95%,
    #faf7f2 100%
  );
  z-index: 2;
  pointer-events: none;
}

@media (max-width: 768px) {
  .clients-slider {
    height: 100px;
  }
  
  .client-slide {
    flex: 0 0 160px;
    padding: 0 15px;
  }
  
  .client-logo-wrapper {
    width: 130px;
    height: 65px;
  }
}

/* Footer Styles */
.cyber-footer {
  position: relative;
  background: #faf7f2 !important;
  padding: 60px 0 20px;
  overflow: hidden;
  border-top: 1px solid rgba(183, 139, 68, 0.2);
}

.footer-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(to right, rgba(0, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.1;
}

.footer-glow {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  box-shadow: 0 0 20px var(--primary-color);
}

.footer-logo-section {
  text-align: center;
}

.footer-logo {
  width: 180px;
  height: auto;
  margin-bottom: 1rem;
  filter: brightness(1.2);
}

.footer-tagline {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin-top: 1rem;
}

.cyber-box {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 10px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.cyber-box:hover {
  border-color: var(--primary-color);
  box-shadow: 0 0 30px var(--primary-glow);
}

.footer-title {
  color: var(--primary-color);
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
 
  text-align: center;
}

.quick-links {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.social-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.social-icon {
  text-decoration: none;
}

.icon-box {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.icon-box .v-icon {
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
}

.icon-box:hover {
  border-color: var(--primary-color);
  transform: translateY(-5px);
  box-shadow: 0 0 20px var(--primary-glow);
}

.icon-box:hover .v-icon {
  color: var(--primary-color);
}

.footer-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent);
  margin: 2rem 0;
}

.footer-bottom {
  text-align: center;
}

.copyright {
  color: #b78b44;
  font-size: 0.9rem;
}

@media (max-width: 960px) {
  .cyber-footer {
    padding: 40px 0 20px;
  }
  
  .footer-logo-section,
  .footer-links,
  .footer-social {
    margin-bottom: 2rem;
  }
  
  .social-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 600px) {
  .social-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .footer-logo {
    width: 140px;
  }
}

/* Update these background-related styles */

/* Main background */
.v-main {
  background-color: #faf7f2 !important; /* Warm white main background */
}

/* Section backgrounds */
.cyber-section {
  background: rgba(255, 255, 255, 0.9); /* Light background for sections */
  box-shadow: 
    0 0 30px rgba(183, 139, 68, 0.15),
    0 0 60px rgba(183, 139, 68, 0.1),
    inset 0 0 30px rgba(183, 139, 68, 0.05);
  border: 1px solid rgba(183, 139, 68, 0.2);
}

/* Card and container backgrounds */
.tech-card,
.cyber-service-card,
.cyber-form-container,
.info-item,
.cyber-text-box,
.cyber-image-frame,
.logo-frame {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

/* Update text colors for better contrast */
.cyber-text,
.service-desc,
.info-content p {
  color: #2c2c2c;
}

/* Footer background */
.cyber-footer {
  background: #faf7f2 !important;
}

/* Navigation background */
.floating-nav {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(183, 139, 68, 0.2);
}

.nav-item {
  color: #2c2c2c;
}

/* Clients slider background */
.clients-slider {
  background: rgba(255, 255, 255, 0.95);
}

/* Section divider */
.section-divider {
  background: linear-gradient(to bottom, 
    rgba(250, 247, 242, 1) 0%,
    rgba(183, 139, 68, 0.05) 50%,
    rgba(250, 247, 242, 1) 100%
  );
}

/* Add new animation */
@keyframes titleShine {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

/* Update the contact form input styles */
.cyber-input {
  background: rgba(255, 255, 255, 0.95) !important;
  border-color: rgba(183, 139, 68, 0.2) !important;
  color: #b78b44 !important;
}

.cyber-input :deep(.v-field__outline) {
  color: rgba(183, 139, 68, 0.3) !important;
}

.cyber-input :deep(.v-field__input) {
  color: #b78b44 !important;
}

.cyber-input :deep(.v-label) {
  color: rgba(183, 139, 68, 0.7) !important;
}

/* Update form container background */
.cyber-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(183, 139, 68, 0.2);
}

/* Update placeholder text color */
.cyber-input :deep(input::placeholder),
.cyber-input :deep(textarea::placeholder) {
  color: rgba(183, 139, 68, 0.5) !important;
}

/* Update focus state */
.cyber-input:focus {
  border-color: #b78b44 !important;
  box-shadow: 0 0 10px rgba(183, 139, 68, 0.2);
}

/* Update hover state */
.cyber-input:hover {
  border-color: rgba(183, 139, 68, 0.5) !important;
}

/* Add smooth scroll behavior to html */
html {
  scroll-behavior: smooth;
  scroll-padding-top: 50px; /* Adjust if you have a fixed header */
}

/* Optional: Add a transition to sections for smoother appearance */
.cyber-section {
  transition: opacity 0.3s ease;
  /* ... existing styles ... */
}

.address-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.address-line {
  margin: 0;
  line-height: 1.4;
}

.address-line.rtl {
  direction: rtl;
}

/* Update the copyright text color */
.copyright {
  color: #b78b44;
  font-size: 0.9rem;
}

/* Update hover state if needed */
.footer-copyright:hover .copyright {
  color: rgba(183, 139, 68, 0.8);
}

/* Add these styles to fix Arabic text rendering */
[lang="ar"],
.ar-text,
.cyber-title[data-text*="ن"],  /* Target Arabic titles */
.cyber-subtitle:lang(ar),
.nav-text:lang(ar) {
  font-family: 'Cairo', sans-serif !important;
  letter-spacing: 0 !important; /* Remove letter spacing for Arabic */
  word-spacing: 2px; /* Add slight word spacing instead */
}

/* Update the title styles to handle Arabic better */
.cyber-title {
  font-size: 3rem;
  color: var(--primary-color);
  text-transform: uppercase;
  margin: 0;
  position: relative;
  text-shadow: 0 0 10px var(--primary-glow);
}

/* Add Arabic-specific letter-spacing override */
[dir="rtl"] .cyber-title {
  letter-spacing: 0 !important;
  word-spacing: 2px;
}
</style>
